import {
  AfterViewInit,
  Component,
  OnDestroy,
  inject,
  ChangeDetectionStrategy,
  NgZone,
  input,
} from '@angular/core';
import Phaser from 'phaser';
import { AnalyticsService } from 'src/app/core/services/analytics/analytics.service';
import { EventName } from 'src/app/core/services/analytics/event-names';
import { UxGameController } from '../services/controllers/game-legacy/ux-game-controller';
import { Subject, takeUntil } from 'rxjs';
import { GenericSceneController } from '../services/controllers/scene-controllers/scene-controller-factory';
import { WebGlError } from 'src/app/shared/errors/webgl-error';

@Component({
  selector: 'app-game-screen',
  templateUrl: './game-screen.component.html',
  styleUrls: ['./game-screen.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GameScreenComponent implements AfterViewInit, OnDestroy {
  readonly gameController = input.required<UxGameController | GenericSceneController>();

  private analyticsService = inject(AnalyticsService);
  private ngZone = inject(NgZone);

  private readonly destroy$ = new Subject<void>();
  private phaserGame?: Phaser.Game;

  ngAfterViewInit(): void {
    this.analyticsService.log(EventName.ClinchGameViewInit);

    this.ngZone.runOutsideAngular(() => {
      try {
        const gameWidth = Math.min(
          window.innerWidth,
          window.innerHeight < 800 ? (window.innerHeight - 0) * 0.75 : window.innerWidth,
        );
        const gameHeight = window.innerHeight - 200;

        this.phaserGame = new Phaser.Game({
          type: Phaser.AUTO, // Use AUTO render which will default to WEBGL if available
          scene: this.gameController().allScenes,
          transparent: true,
          roundPixels: false, // Disable round pixels to allow interpolation to use fractional pixels for smooth animation
          input: {
            windowEvents: false, // Don't listen to window events as well as canvas events.
          },
          scale: {
            mode: Phaser.Scale.NONE,
            parent: 'gameCanvas',
            width: gameWidth,
            height: gameHeight,
          },
          audio: {
            noAudio: true,
          },
          render: {
            powerPreference: 'high-performance',
          },
        });

        // Phaser.AUTO silently falls back to Canvas if WebGL context creation
        // fails. The card games render with Planes (WebGL-only), so surface
        // the fallback rather than letting cards silently not render.
        this.phaserGame.events.once(Phaser.Core.Events.READY, () => {
          if (this.phaserGame?.renderer.type === Phaser.CANVAS) {
            this.analyticsService.handleError(
              new WebGlError(
                'Phaser fell back to the Canvas renderer; WebGL context creation failed.',
              ),
              'Detect Phaser renderer fallback',
            );
          }
        });

        this.gameController()
          .inputEnabled$.pipe(takeUntil(this.destroy$))
          .subscribe((enabled) => {
            if (this.phaserGame) {
              this.phaserGame.input.enabled = enabled;
            }
          });
      } catch (error) {
        this.analyticsService.handleError(error, 'Construct new Phaser game');
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    this.phaserGame?.destroy(true);
  }
}
